// ─────────────────────────────────────────────────────────────────────────────
// Wardens — the social layer for the Conjugarden.
//
// There is no multiplayer backend in this app (Auth.js handles identity; there
// is no shared user DB). So this module is a self-contained "data source": a
// seeded roster of fellow Wardens, locally-persisted friends + profile, and
// achievements computed from your garden. It is shaped like an API client so it
// can later be swapped for real server calls without touching the UI.
// ─────────────────────────────────────────────────────────────────────────────
import { type GardenState, masteredSpecimens, gardenLevel } from './garden.js'
import { restorationPct } from './garden-story.js'

export type Player = {
  id: string
  handle: string
  avatar: string
  specimens: number
  biomesCleared: number
  bestStreak: number
  restoration: number     // 0..100
  pollen: number
  wins: number
  totalCorrect: number
  lastActive: string      // human label
  isYou?: boolean
  bio?: string
}

// Title earned from how much of the Verdant a Warden has sung back.
export function wardenTitle(restoration: number): string {
  if (restoration >= 100) return 'Verdant Sovereign'
  if (restoration >= 85) return 'Hushbane'
  if (restoration >= 65) return 'Verdant Warden'
  if (restoration >= 45) return 'Grovekeeper'
  if (restoration >= 25) return 'Greenhand'
  if (restoration >= 10) return 'Sprout-Warden'
  return 'Ashling'
}

// A single comparable number across every Warden.
export function wardenScore(p: Pick<Player, 'restoration' | 'specimens' | 'biomesCleared' | 'bestStreak' | 'wins' | 'totalCorrect'>): number {
  return Math.round(p.restoration * 40 + p.specimens * 12 + p.biomesCleared * 60 + p.bestStreak * 5 + p.wins * 30 + p.totalCorrect * 0.5)
}

// ── Seeded roster of fellow Wardens ──────────────────────────────────────────
export const ROSTER: Player[] = [
  { id: 'w-lluvia',   handle: 'lluvia',         avatar: '🌧️', specimens: 96, biomesCleared: 5, bestStreak: 61, restoration: 100, pollen: 18400, wins: 41, totalCorrect: 3120, lastActive: '2m ago' },
  { id: 'w-mossw',    handle: 'MossWeaver',     avatar: '🌿', specimens: 88, biomesCleared: 5, bestStreak: 54, restoration: 98,  pollen: 15900, wins: 37, totalCorrect: 2760, lastActive: 'just now' },
  { id: 'w-kongas',   handle: 'köngäs',         avatar: '❄️', specimens: 74, biomesCleared: 5, bestStreak: 48, restoration: 94,  pollen: 13200, wins: 33, totalCorrect: 2410, lastActive: '14m ago' },
  { id: 'w-saudade',  handle: 'saudade.exe',    avatar: '🌊', specimens: 70, biomesCleared: 4, bestStreak: 52, restoration: 91,  pollen: 12050, wins: 29, totalCorrect: 2230, lastActive: '1h ago' },
  { id: 'w-terra',    handle: 'TerraVerde',     avatar: '🌳', specimens: 63, biomesCleared: 4, bestStreak: 39, restoration: 86,  pollen: 10800, wins: 27, totalCorrect: 1990, lastActive: '3h ago' },
  { id: 'w-sienna',   handle: 'Sienna Vale',    avatar: '🍂', specimens: 58, biomesCleared: 4, bestStreak: 44, restoration: 82,  pollen: 9600,  wins: 24, totalCorrect: 1820, lastActive: 'today' },
  { id: 'w-jardin',   handle: 'el_jardinero',   avatar: '🌵', specimens: 51, biomesCleared: 3, bestStreak: 35, restoration: 76,  pollen: 8400,  wins: 21, totalCorrect: 1610, lastActive: 'today' },
  { id: 'w-fern',     handle: 'fern_and_far',   avatar: '🌒', specimens: 47, biomesCleared: 3, bestStreak: 41, restoration: 71,  pollen: 7700,  wins: 19, totalCorrect: 1480, lastActive: 'yesterday' },
  { id: 'w-marisol',  handle: 'marisol',        avatar: '🌻', specimens: 42, biomesCleared: 3, bestStreak: 28, restoration: 66,  pollen: 6600,  wins: 17, totalCorrect: 1310, lastActive: 'yesterday' },
  { id: 'w-bram',     handle: 'old_bram',       avatar: '🪵', specimens: 39, biomesCleared: 3, bestStreak: 33, restoration: 61,  pollen: 6100,  wins: 15, totalCorrect: 1240, lastActive: '2d ago' },
  { id: 'w-clover',   handle: 'cloverfield',    avatar: '🍀', specimens: 34, biomesCleared: 2, bestStreak: 26, restoration: 55,  pollen: 5200,  wins: 13, totalCorrect: 1080, lastActive: '2d ago' },
  { id: 'w-pax',      handle: 'paxflora',       avatar: '🌸', specimens: 30, biomesCleared: 2, bestStreak: 30, restoration: 50,  pollen: 4700,  wins: 11, totalCorrect: 960,  lastActive: '3d ago' },
  { id: 'w-duneen',   handle: 'dúnedora',       avatar: '🏵️', specimens: 26, biomesCleared: 2, bestStreak: 22, restoration: 44,  pollen: 3900,  wins: 9,  totalCorrect: 840,  lastActive: '3d ago' },
  { id: 'w-rowan',    handle: 'rowan',          avatar: '🌹', specimens: 22, biomesCleared: 2, bestStreak: 19, restoration: 38,  pollen: 3200,  wins: 8,  totalCorrect: 720,  lastActive: '4d ago' },
  { id: 'w-tito',     handle: 'tito.raíz',      avatar: '🥕', specimens: 18, biomesCleared: 1, bestStreak: 24, restoration: 33,  pollen: 2600,  wins: 6,  totalCorrect: 610,  lastActive: '5d ago' },
  { id: 'w-aspen',    handle: 'aspenwhisper',   avatar: '🍃', specimens: 15, biomesCleared: 1, bestStreak: 16, restoration: 28,  pollen: 2100,  wins: 5,  totalCorrect: 520,  lastActive: '5d ago' },
  { id: 'w-pip',      handle: 'pip',            avatar: '🌱', specimens: 12, biomesCleared: 1, bestStreak: 14, restoration: 22,  pollen: 1600,  wins: 4,  totalCorrect: 430,  lastActive: '1w ago' },
  { id: 'w-juni',     handle: 'juniper_lee',    avatar: '🫐', specimens: 9,  biomesCleared: 1, bestStreak: 18, restoration: 17,  pollen: 1200,  wins: 3,  totalCorrect: 350,  lastActive: '1w ago' },
  { id: 'w-figo',     handle: 'figo',           avatar: '🌰', specimens: 6,  biomesCleared: 0, bestStreak: 11, restoration: 12,  pollen: 760,   wins: 2,  totalCorrect: 240,  lastActive: '2w ago' },
  { id: 'w-mira',     handle: 'mira_m',         avatar: '🌼', specimens: 4,  biomesCleared: 0, bestStreak: 9,  restoration: 8,   pollen: 420,   wins: 1,  totalCorrect: 160,  lastActive: '2w ago' },
  { id: 'w-noa',      handle: 'noa',            avatar: '🪴', specimens: 2,  biomesCleared: 0, bestStreak: 6,  restoration: 4,   pollen: 180,   wins: 0,  totalCorrect: 90,   lastActive: '3w ago' },
  { id: 'w-sprout1',  handle: 'newleaf',        avatar: '☘️', specimens: 1,  biomesCleared: 0, bestStreak: 4,  restoration: 2,   pollen: 60,    wins: 0,  totalCorrect: 40,   lastActive: 'last month' },
]

export function youAsPlayer(state: GardenState, profile: Profile): Player {
  const biomesCleared = Object.values(state.clears).filter(c => (c ?? 0) > 0).length
  return {
    id: 'you', handle: profile.handle, avatar: profile.avatar, isYou: true,
    specimens: masteredSpecimens(state).length,
    biomesCleared,
    bestStreak: state.stats.bestStreak,
    restoration: restorationPct(state),
    pollen: state.stats.totalPollen,
    wins: state.stats.wins,
    totalCorrect: state.stats.totalCorrect,
    lastActive: 'now',
    bio: profile.bio,
  }
}

export function leaderboard(state: GardenState, profile: Profile, scope: 'global' | 'friends'): (Player & { rank: number; score: number })[] {
  const you = youAsPlayer(state, profile)
  let field = [...ROSTER]
  if (scope === 'friends') field = field.filter(p => profile.friends.includes(p.id))
  const all = [...field, you]
  return all
    .map(p => ({ ...p, score: wardenScore(p) }))
    .sort((a, b) => b.score - a.score)
    .map((p, i) => ({ ...p, rank: i + 1 }))
}

export function playerById(id: string): Player | undefined {
  return ROSTER.find(p => p.id === id)
}

// ── Achievements (computed from the garden) ──────────────────────────────────
export type Achievement = {
  id: string
  name: string
  desc: string
  glyph: string
  tier: 'bronze' | 'silver' | 'gold'
  test: (s: GardenState) => boolean
}

export const ACHIEVEMENTS: Achievement[] = [
  { id: 'first-sprout', name: 'First Sprout',   glyph: '🌱', tier: 'bronze', desc: 'Grow your first specimen.',                    test: s => masteredSpecimens(s).length >= 1 },
  { id: 'green-thumb',  name: 'Green Thumb',    glyph: '🌿', tier: 'bronze', desc: 'Grow 10 specimens.',                          test: s => masteredSpecimens(s).length >= 10 },
  { id: 'botanist',     name: 'Botanist',       glyph: '🪴', tier: 'silver', desc: 'Grow 25 specimens.',                          test: s => masteredSpecimens(s).length >= 25 },
  { id: 'curator',      name: 'Curator',        glyph: '🌳', tier: 'gold',   desc: 'Grow 50 specimens.',                          test: s => masteredSpecimens(s).length >= 50 },
  { id: 'first-light',  name: 'First Light',    glyph: '🌅', tier: 'bronze', desc: 'Clear your first biome.',                     test: s => s.stats.wins >= 1 },
  { id: 'pathfinder',   name: 'Pathfinder',     glyph: '🧭', tier: 'silver', desc: 'Clear 3 different biomes.',                   test: s => Object.values(s.clears).filter(c => (c ?? 0) > 0).length >= 3 },
  { id: 'cartographer', name: 'Cartographer',   glyph: '🗺️', tier: 'gold',  desc: 'Clear every biome at least once.',            test: s => Object.values(s.clears).filter(c => (c ?? 0) > 0).length >= 5 },
  { id: 'unbroken',     name: 'Unbroken',       glyph: '🔗', tier: 'bronze', desc: 'Reach a 15 bloom streak.',                    test: s => s.stats.bestStreak >= 15 },
  { id: 'wildfire',     name: 'Wildfire',       glyph: '🔥', tier: 'silver', desc: 'Reach a 30 bloom streak.',                    test: s => s.stats.bestStreak >= 30 },
  { id: 'survivor',     name: 'Survivor',       glyph: '🛡️', tier: 'bronze', desc: 'Win an expedition without withering.',        test: s => s.stats.wins >= 1 },
  { id: 'persistent',   name: 'Persistent',     glyph: '⛏️', tier: 'bronze', desc: 'Mount 10 expeditions.',                       test: s => s.stats.runs >= 10 },
  { id: 'dedicated',    name: 'Dedicated',      glyph: '🏕️', tier: 'silver', desc: 'Mount 50 expeditions.',                       test: s => s.stats.runs >= 50 },
  { id: 'word-speaker', name: 'Word-Speaker',   glyph: '🗣️', tier: 'silver', desc: 'Speak 250 words back to the Verdant.',        test: s => s.stats.totalCorrect >= 250 },
  { id: 'orator',       name: 'Orator',         glyph: '📜', tier: 'gold',   desc: 'Speak 1,000 words back.',                     test: s => s.stats.totalCorrect >= 1000 },
  { id: 'pollen-baron', name: 'Pollen Baron',   glyph: '💰', tier: 'silver', desc: 'Bank 1,000 lifetime Pollen.',                 test: s => s.stats.totalPollen >= 1000 },
  { id: 'hoarder',      name: 'Hoarder',        glyph: '🏦', tier: 'gold',   desc: 'Bank 5,000 lifetime Pollen.',                 test: s => s.stats.totalPollen >= 5000 },
  { id: 'hush-breaker', name: 'Hush-Breaker',   glyph: '🌗', tier: 'silver', desc: 'Restore the Verdant to 50%.',                 test: s => restorationPct(s) >= 50 },
  { id: 'verdant',      name: 'Verdant',        glyph: '🌍', tier: 'gold',   desc: 'Restore the Verdant fully. Break the Hush.',  test: s => restorationPct(s) >= 100 },
  { id: 'trial-victor', name: 'Trial Victor',   glyph: '🏆', tier: 'bronze', desc: 'Conquer your first Conjugation Trial.',       test: s => (s.stats.bosses ?? 0) >= 1 },
  { id: 'trialmaster',  name: 'Trialmaster',    glyph: '🎖️', tier: 'gold',  desc: 'Conquer 25 Conjugation Trials.',              test: s => (s.stats.bosses ?? 0) >= 25 },
  { id: 'deep-diver',   name: 'Deep Diver',     glyph: '🌀', tier: 'silver', desc: 'Clear a biome at Depth 3 of the Hush or deeper.', test: s => Math.max(0, ...Object.values(s.depth ?? {}).map(d => d ?? 0)) >= 3 },
  { id: 'heartwood',    name: 'Heartwarden',    glyph: '🪵', tier: 'gold',   desc: 'Amass 40 Heartwood.',                         test: s => (s.heartwood ?? 0) >= 40 },
  { id: 'healer',       name: 'Field Medic',    glyph: '🩹', tier: 'silver', desc: 'Shore up 50 struggling verbs on expeditions.', test: s => (s.stats.reviewFixed ?? 0) >= 50 },
]

export function evaluateAchievements(s: GardenState): { def: Achievement; unlocked: boolean }[] {
  return ACHIEVEMENTS.map(def => ({ def, unlocked: def.test(s) }))
}
export function unlockedAchievements(s: GardenState): Achievement[] {
  return ACHIEVEMENTS.filter(a => a.test(s))
}
export function newlyUnlocked(s: GardenState): Achievement[] {
  const seen = new Set(s.seenAchievements)
  return ACHIEVEMENTS.filter(a => a.test(s) && !seen.has(a.id))
}

// ── Profile (global identity, persisted across languages) ────────────────────
export type Profile = { handle: string; avatar: string; friends: string[]; createdAt: number; bio: string }

const PROFILE_KEY = 'c8-warden-v1'
export const AVATARS = ['🌿', '🌱', '🌳', '🌸', '🍃', '🌵', '🌻', '🍄', '🌷', '🪴', '🌾', '🦋', '🐝', '🌲', '🍀', '🌺']

export function readProfile(authName?: string | null): Profile {
  const fallback = (): Profile => ({
    handle: (authName && authName.trim()) ? authName.trim().split(/\s+/)[0] : 'Warden-' + Math.floor(1000 + Math.random() * 9000),
    avatar: AVATARS[Math.floor(Math.random() * AVATARS.length)],
    friends: [], createdAt: Date.now(), bio: '',
  })
  if (typeof window === 'undefined') return { handle: 'Warden', avatar: '🌿', friends: [], createdAt: 0, bio: '' }
  try {
    const raw = window.localStorage.getItem(PROFILE_KEY)
    if (!raw) { const p = fallback(); window.localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); return p }
    const parsed = JSON.parse(raw) as Partial<Profile>
    return { handle: parsed.handle ?? 'Warden', avatar: parsed.avatar ?? '🌿', friends: parsed.friends ?? [], createdAt: parsed.createdAt ?? Date.now(), bio: parsed.bio ?? '' }
  } catch {
    return fallback()
  }
}

export function writeProfile(p: Profile): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(PROFILE_KEY, JSON.stringify(p)) } catch {}
}
